import { ChangeEvent, memo, useCallback } from 'react'

import { Save } from '@mui/icons-material'
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  FormControl,
  InputBase,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  styled,
  TextField,
  Typography,
} from '@mui/material'
import {
  DistributedTaskLogEntry,
  EventsEventIdLogsGetLevelsEnum,
  EventsEventIdLogsGetTypesEnum,
} from '@nativewaves/nw-cloud-sdk/smep'
import { useNavigate } from '@tanstack/react-router'
import { format, setHours, setMinutes } from 'date-fns'
import { omit } from 'lodash-es'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import SimpleBar from 'simplebar-react'

import { DatePicker } from '@shared/components/DateTimePicker'
import { FoldoutPanel, useCloseHandler } from '@shared/components/Foldout'
import { Kbd } from '@shared/components/MaterialUIEnhancements'
import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'
import { eventMonitoringRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'

const parseNewDate = (time: string, baseDate: Date) => {
  const [hours, minutes] = time.split(':').map((str) => parseInt(str, 10))
  return setHours(setMinutes(baseDate, minutes), hours)
}

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'max-content minmax(min-content, 1fr)',
  gridAutoRows: 'minmax(32px, auto)',
  alignItems: 'center',
  justifyContent: 'stretch',
  columnGap: theme.spacing(4),
  rowGap: theme.spacing(3),
}))

const TimePicker = styled(InputBase)(({ theme }) => ({
  ...theme.typography['heading-base'],
  lineHeight: 1,
  borderRadius: 1,
  backgroundColor: theme.palette.background.levels[500],
  padding: theme.spacing(1),
  margin: theme.spacing(-1),

  '& input[type="time"]::-webkit-calendar-picker-indicator': {
    fontSize: 24,
    marginRight: theme.spacing(0.5),
    cursor: 'pointer',
  },
}))

const typeOptions = {
  Log: EventsEventIdLogsGetTypesEnum.Log,
  'Alert New': EventsEventIdLogsGetTypesEnum.AlertCreation,
  'Alert Resolved': EventsEventIdLogsGetTypesEnum.AlertDeletion,
  'Alert Update': EventsEventIdLogsGetTypesEnum.AlertUpdate,
}

type FormStructure = {
  levels?: EventsEventIdLogsGetLevelsEnum[]
  types?: EventsEventIdLogsGetTypesEnum[]
  from?: number
  to?: number
  alertId?: string
  alertCode?: string
}

const FilterPanelRaw = () => {
  const handleClose = useCloseHandler()
  const { t } = useTranslation(['common', 'domain'])

  const navigate = useNavigate({ from: eventMonitoringRoute.fullPath })
  const search = eventMonitoringRoute.useSearch()

  const { handleSubmit, control, watch, reset } = useForm<FormStructure>({
    defaultValues: { ...omit(search, 'query') },
  })

  const fromField = watch('from')
  const toField = watch('to')

  const getTypeLabel = useCallback((logType: DistributedTaskLogEntry['type']) => {
    return (Object.keys(typeOptions) as Array<keyof typeof typeOptions>).find((key) => typeOptions[key] === logType)
  }, [])

  const handleDateRangeChange = useCallback((date: Date | null) => {
    if (date) {
      return date.valueOf() / 1000
    } else {
      return null
    }
  }, [])

  const handleTimeRangeChange = useCallback((e: ChangeEvent<HTMLInputElement>, value: number) => {
    const time = e.target.value

    return parseNewDate(time, new Date(value * 1000)).valueOf() / 1000
  }, [])

  const handleSave = useCallback(
    (data: FormStructure) => {
      const query = search.query

      const filters = Object.fromEntries(
        Object.entries(data).filter(([, value]) => (Array.isArray(value) ? value.length > 0 : Boolean(value))),
      )

      navigate({
        search: {
          ...(query && { query }),
          ...filters,
        },
      })

      handleClose({}, 'escapeKeyDown')
    },
    [handleClose, navigate, search.query],
  )

  return (
    <FoldoutPanel
      title={<Typography variant="heading-lg">{t('domain:Workspace.Event.filterOptions')}</Typography>}
      breadcrumbs={['Logs']}
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
          <Button
            variant="contained"
            size="small"
            endIcon={<Save fontSize="inherit" />}
            onClick={handleSubmit(handleSave)}
          >
            {t('save')}
          </Button>
          <Divider orientation="vertical" flexItem />

          <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={() => reset({ alertCode: '', alertId: '', from: undefined, to: undefined, levels: [], types: [] })}
          >
            {t('reset')}
          </Button>

          <ButtonBase onClick={() => handleClose({}, 'escapeKeyDown')}>
            <Kbd sx={{ py: 0 }}>Esc</Kbd>
          </ButtonBase>
        </Box>
      }
    >
      <ContentWrapper>
        <Typography variant="text">{t('domain:Workspace.Event.alertId')}:</Typography>
        <Controller control={control} name="alertId" render={({ field }) => <TextField {...field} size="small" />} />

        <Typography variant="text">{t('domain:Workspace.Event.alertCode')}:</Typography>
        <Controller control={control} name="alertCode" render={({ field }) => <TextField {...field} size="small" />} />

        <Typography variant="text">{t('domain:Workspace.Event.types')}:</Typography>
        <Controller
          control={control}
          name="types"
          render={({ field }) => (
            <FormControl sx={{ flex: 1 }}>
              <Select
                {...field}
                value={field.value || []}
                multiple
                size="small"
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <SimpleBar>
                    <Box sx={{ display: 'flex', columnGap: 1 }}>
                      {selected.map((value) => (
                        <CodeInline sx={{ py: 0 }} key={value}>
                          {getTypeLabel(value)}
                        </CodeInline>
                      ))}
                    </Box>
                  </SimpleBar>
                )}
              >
                {Object.entries(typeOptions).map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Typography variant="text">{t('domain:Workspace.Event.levels')}:</Typography>
        <Controller
          control={control}
          name="levels"
          render={({ field }) => (
            <FormControl sx={{ flex: 1 }}>
              <Select
                {...field}
                value={field.value || []}
                multiple
                size="small"
                input={<OutlinedInput />}
                renderValue={(selected) => (
                  <SimpleBar>
                    <Box sx={{ display: 'flex', columnGap: 1 }}>
                      {selected.map((value) => (
                        <CodeInline sx={{ py: 0 }} key={value}>
                          {value}
                        </CodeInline>
                      ))}
                    </Box>
                  </SimpleBar>
                )}
              >
                {Object.entries(EventsEventIdLogsGetLevelsEnum).map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />

        <Paper sx={{ m: -1, p: 1, gridColumn: '1 / span 2', bgcolor: 'background.levels.100' }}>
          <Typography variant="heading-md">{t('domain:Workspace.Event.timeRanges')}</Typography>
        </Paper>

        <Typography variant="text">{t('domain:Workspace.Event.from')}:</Typography>
        <Controller
          control={control}
          name="from"
          render={({ field }) => (
            <DatePicker
              sx={{ borderRadius: 1, bgcolor: 'background.levels.500', p: 1, m: -1 }}
              defaultDate={field.value ? new Date(field.value * 1000) : undefined}
              onChange={(e) => field.onChange(handleDateRangeChange(e))}
            />
          )}
        />

        {fromField && (
          <Controller
            control={control}
            name="from"
            render={({ field }) => (
              <TimePicker
                sx={{ gridColumn: 2 }}
                type="time"
                defaultValue={field.value ? format(new Date(field.value * 1000), 'HH:mm') : '00:00'}
                onChange={(e: ChangeEvent<HTMLInputElement>) => field.onChange(handleTimeRangeChange(e, field.value!))}
              />
            )}
          />
        )}

        <Typography variant="text">{t('domain:Workspace.Event.until')}:</Typography>
        <Controller
          control={control}
          name="to"
          render={({ field }) => (
            <DatePicker
              sx={{ borderRadius: 1, bgcolor: 'background.levels.500', p: 1, m: -1 }}
              onChange={(e) => field.onChange(handleDateRangeChange(e))}
              defaultDate={field.value ? new Date(field.value * 1000) : undefined}
            />
          )}
        />

        {toField && (
          <Controller
            control={control}
            name="to"
            render={({ field }) => (
              <TimePicker
                sx={{ gridColumn: 2 }}
                type="time"
                defaultValue={field.value ? format(new Date(field.value * 1000), 'HH:mm') : '00:00'}
                onChange={(e: ChangeEvent<HTMLInputElement>) => field.onChange(handleTimeRangeChange(e, field.value!))}
              />
            )}
          />
        )}
      </ContentWrapper>
    </FoldoutPanel>
  )
}

export const FilterPanel = memo(FilterPanelRaw)
